import "@/App.css";
import { applyStoredTheme } from "@/components/ThemeToggle";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import ExecutiveSummary from "@/pages/ExecutiveSummary";
import Validation from "@/pages/Validation";
import { UsersPage, AuditPage } from "@/pages/AdminPages";
import { RevenuePage, ExpensesPage, CapexPage } from "@/pages/ChartPages";
import CompareVersions from "@/pages/CompareVersions";
import { PageHeader } from "@/components/Widgets";
import { Toaster } from "sonner";
import { DataProvider, useData } from "@/context/DataContext";
import Layout from "@/components/Layout";
import Overview from "@/pages/Overview";
import Entities from "@/pages/Entities";
import CashFlow from "@/pages/CashFlow";
import Loans from "@/pages/Loans";
import Scenarios from "@/pages/Scenarios";
import Assumptions from "@/pages/Assumptions";
import DataVersions from "@/pages/DataVersions";
import MappingReview from "@/pages/MappingReview";

const ComparePage = () => {
  const { data, filters } = useData();
  const fe = data.model.entities.find((x) => x.code === filters.entity);
  return <div data-testid="compare-page"><PageHeader kicker="Any two confirmed versions" title={fe ? `Version Comparison - ${fe.short}` : "Version Comparison"} /><CompareVersions /></div>;
};

const Guard = ({ children, roles }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex min-h-screen items-center justify-center bg-[#0B0F19] text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const Protected = () => (
  <Guard>
    <DataProvider><Layout /></DataProvider>
  </Guard>
);

applyStoredTheme();

function App() {
  return (
    <div className="App dark">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="login" element={<Login />} />
            <Route element={<Protected />}>
              <Route index element={<ExecutiveSummary />} />
              <Route path="group" element={<Overview />} />
              <Route path="revenue" element={<RevenuePage />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="capex" element={<CapexPage />} />
              <Route path="compare" element={<ComparePage />} />
              <Route path="validation" element={<Validation />} />
              <Route path="audit" element={<Guard roles={["admin", "modeller"]}><AuditPage /></Guard>} />
              <Route path="users" element={<Guard roles={["admin"]}><UsersPage /></Guard>} />
              <Route path="entities" element={<Entities />} />
              <Route path="entities/:code" element={<Entities />} />
              <Route path="cash-flow" element={<CashFlow />} />
              <Route path="loans" element={<Loans />} />
              <Route path="scenarios" element={<Scenarios />} />
              <Route path="assumptions" element={<Assumptions />} />
              <Route path="data" element={<DataVersions />} />
              <Route path="data/review/:id" element={<MappingReview />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
